import React, { useRef, useMemo, MutableRefObject, useState, useCallback, useEffect, MouseEvent } from 'react';
import { Callout } from 'components/layout/Callout';
import { IDesignerSubAction, DesignerSubActions } from './DesignerSubActions';
import { HasOpenState } from 'hooks/HasOpenState';
import classNames from 'classnames';
import './DesignerAction.less';
import { DesignerActionsSeparator } from './DesignerActionsSeparator';
export interface IDesignerActionProps extends IDesignerAction {}
//TODO alex: add "order" property
export interface IDesignerAction {
  /**
   * Action name
   */
  name: string;
  /**
   * Action title will be shown on hover
   */
  title?: string;
  /**
   * Executes when click on action.
   * If action is not provided, sub actions will be shown on action click.
   */
  action?: ((event?: MouseEvent<HTMLButtonElement>) => void | Promise<any>) | (() => void | Promise<any>);
  id?: string;
  subActions?: IDesignerSubAction[] | (() => Promise<IDesignerSubAction[] | undefined>);
  disabled?: boolean;
  separator?: separatorType;
}

export enum separatorType {
  left,
  right,
  both,
}

function getActionClassName(disabled?: boolean, active?: boolean) {
  return classNames('dpc-designer-action', {
    'dpc-designer-action--disable': !!disabled,
    'dpc-designer-action--active': !!active,
  });
}

function getButtonClassName(disabled?: boolean) {
  return classNames('dpc-designer-action__button', {
    'dpc-designer-action__button--disable': !!disabled,
  });
}

function getMoreClassName(disabled?: boolean) {
  return classNames('dpc-designer-action__more', {
    'dpc-designer-action__more--disable': !!disabled,
  });
}

export function DesignerAction({
  name,
  action,
  separator,
  subActions: _subActions,
  title,
  disabled: _disabled = false,
}: IDesignerActionProps) {
  const buttonRef: MutableRefObject<HTMLLIElement | null> = useRef(null);
  const [subActions, setSubActions] = useState<IDesignerSubAction[]>([]);
  const { isOpen, setClosed, setOpen } = HasOpenState(false);
  const [disabled, setDisabled] = useState(_disabled);
  useEffect(() => setDisabled(_disabled), [_disabled]);

  const toggleOpen = useCallback(async () => {
    let subActions: IDesignerSubAction[] | undefined;
    if (typeof _subActions === 'function') {
      try {
        setDisabled(true);
        subActions = await _subActions();
      } finally {
        setDisabled(false);
      }
    } else if (Array.isArray(_subActions)) {
      subActions = _subActions;
    }
    setSubActions(subActions ?? []);
    setOpen();
  }, [setOpen, _subActions]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (action) {
      action?.(event);
    } else {
      toggleOpen();
    }
  };

  const noActions = useMemo(
    () =>
      [
        {
          action: setClosed,
          name: 'No Actions',
          title: 'No Actions',
        },
      ] as IDesignerSubAction[],
    [setClosed]
  );
  const showSubActions: boolean = typeof _subActions === 'function' || Array.isArray(_subActions);
  const hasSubActions = !!subActions?.length;
  return (
    <li ref={buttonRef} className={getActionClassName(disabled, isOpen)}>
      {(separator === separatorType.left || separator === separatorType.both) && (
        <DesignerActionsSeparator className="dpc-designer-action__left-separator" />
      )}
      <button
        disabled={disabled}
        onClick={handleClick}
        className={getButtonClassName(disabled)}
        title={title?.length ? title : name}
      >
        {name}
      </button>
      {showSubActions && (
        <button
          disabled={disabled}
          onClick={toggleOpen}
          className={getMoreClassName(disabled)}
          aria-label={`${name} sub actions`}
        />
      )}
      <Callout isOpen={isOpen} onClose={setClosed} sourceRef={buttonRef} placement="bottom" showArrow>
        <DesignerSubActions close={setClosed} subActions={hasSubActions ? subActions : noActions} />
      </Callout>
      {(separator === separatorType.right || separator === separatorType.both) && (
        <DesignerActionsSeparator className="dpc-designer-action__right-separator" />
      )}
    </li>
  );
}
